import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { strings } from 'src/localization';
import { LoadingProgress, TextField, EmailField, PasswordField, PhoneField, ScrollBar, Dialog } from '@framework-controls/core';
import css from './SignUp.module.scss';
import { CreateAcctInp, Customer, UpdateApptCustInp, ApptCust, ascCtaInteractionInput, ascFormEngagementInput, ascPageviewInput, VehicleRec, SpecData } from 'src/models';
import { IView } from 'Framework';
import { ComponentMapIds } from '../map';
import SVPButton from '../SVPButtons/SVPButton';
import ErrorMessage from 'src/components/ErrorMessage/ErrorMessage';
import RecordLockError from '../RecordLockError/containers/RecordLockError';
import { IconRemove } from 'src/content/icons';
import { ViewMode } from 'src/enums';
import { ascCtaInteraction, ascFormEngagement, ascPageview } from 'src/helpers/EventHelper/EventHelper';
import { sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface IErrorDictionary {
    [field: string]: {
        error: boolean;
        helperText: string;
        isRequired?: boolean;
    }
}

const initialError: IErrorDictionary = {
    'FirstName': { error: false, helperText: '' },
    'LastName': { error: false, helperText: '' },
    'PhoneNumber': { error: false, helperText: '' },
    'Email': { error: false, helperText: '' },
    'Password': { error: false, helperText: '' },
    'Confirm': { error: false, helperText: '' },
};

const emailRegex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))/.source +
    /@/.source +
    /((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);

export interface IProps {
    customerInfo?: Customer;
    apptCust?: ApptCust;
    errMsg?: string;
    profileNum: string;
    store: string;
    branch: string;
    previewFlag: string;
    mainPaneViewOrder: IView[];
    identityVerified: boolean;
    mfaContact: string[];
    mfaEnabled: boolean | undefined;
    viewMode: ViewMode;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    theme?: string;
    locale: string;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    createAccount?: (inp: CreateAcctInp, store: string, branch: string) => void;
    pushToMainPanes: (inputView: IView) => void;
    clearError: () => void;
    onLogin: (cust: CreateAcctInp, profile: string, store: string, branch: string) => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    selectVehicle: (id: string) => void;
    getVehicles: () => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    sendResetEmail: (email: string, nameId: string, profile: string, store: string, branch: string) => void;
    setViewMode: (viewMode: ViewMode) => void;
    clearApptState: () => void;
    clearVehicles: () => void;
    popFromMainPane: (id: string | string[]) => void;
    setApptHistId: (id: string) => void;
}

const SignUp = (props: IProps) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState(initialError);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [creatingAcct, setCreatingAcct] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);
    const [showAccountError, setShowAccountError] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState<boolean>(false);
    const [displaySentMessage, setDisplaySentMessage] = useState<boolean>(false);
    const [nameLengthError, setNameLengthError] = useState<boolean>(false);
    const [tempSelVeh, setTempSelVeh] = useState<string>('');
    const [resumeAppt, setResumeAppt] = useState<boolean>(false);
    const [resumeDone, setResumeDone] = useState<boolean>(false);
    const isLoggingIn = useRef(false);
    const currentScreen = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId;
    const previousScreen = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 2]?.viewId;

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    useEffect(() => {
        if (props.apptCust && !isLoggingIn.current) {
            setFirstName(props.apptCust.custFirstName?.includes('*') ? '' : props.apptCust.custFirstName ?? '');
            setLastName(props.apptCust.custLastName?.includes('*') ? '' : props.apptCust.custLastName ?? '');
            setPhoneNumber(props.apptCust.custPhoneNumber?.includes('*') ? '' : props.apptCust.custPhoneNumber ?? '');
            setEmail(props.apptCust.custEmailAddress?.includes('*') ? '' : props.apptCust.custEmailAddress ?? '');
        }
    }, [props.apptCust, isLoggingIn]);

    // This function handles each of the TextField modifications
    const changeHandler = useCallback((id: string, value: string) => {
        switch (id) {
            case 'LastName':
                setLastName(value);
                break;

            case 'PhoneNumber':
                setPhoneNumber(value);
                break;

            case 'Email':
                setEmail(value);
                break;

            case 'Password':
                setPassword(value);
                break;

            case 'Confirm':
                setConfirm(value);
                break;

            // First Name and Default are the same
            case 'FirstName':
            default:
                setFirstName(value);
                break;
        }
    }, []);

    // Field Validation
    const blurHandler = useCallback((id: string, value: string, isError: boolean) => {
        let hasError = false;
        let helperText = '';
        let fieldErrors = {...errors};
        let elementText = '';
        switch (id) {
            // Name fields cannot be greater than 35 characters in length.
            case 'LastName':
                if ((value + firstName).length > 35) {
                    setNameLengthError(true);
                } else {
                    setNameLengthError(false);
                }
                elementText = strings.LastName;
                break;

            // Passwords must match.
            case 'Password':
                hasError = isError;
                if (confirm && password && confirm !== password && !hasError) {
                    fieldErrors['Confirm'].error = true;
                    fieldErrors['Confirm'].helperText = strings.PasswordMismatch;
                } else {
                    fieldErrors['Confirm'].error = false;
                    fieldErrors['Confirm'].helperText = '';
                }
                elementText = strings.Password;
                break;

            case 'Confirm':
                hasError = isError;
                if (confirm && password && confirm !== password && !hasError && !fieldErrors['Password'].error) {
                    hasError = true;
                    helperText = strings.PasswordMismatch;
                }
                elementText = strings.ConfirmPassword;
                break;

            case 'PhoneNumber':
                hasError = isError;
                elementText = strings.PhoneNumber;
                break;

            case 'FirstName':
                if ((value + lastName).length > 35) {
                    setNameLengthError(true);
                } else {
                    setNameLengthError(false);
                }
                elementText = strings.FirstName;
                break;

            case 'Email':
                elementText = strings.EmailAddress;
                break;

            default:
                break;
        }
        fieldErrors = {
            ...fieldErrors,
            [id]: {
                error: hasError,
                helperText: helperText,
            },
        };
        setErrors(fieldErrors);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: elementText,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [errors, firstName, confirm, password, lastName, props.vehicles, props.selectedVehicle]);

    // Handle account creation
    const createAcct = useCallback((e: any) => {
        if (!!props.createAccount) {
            const inp: CreateAcctInp = { firstName: '', lastName: '', phoneNumber: '', emailAddress: '', pwd: '' };
            inp.firstName = firstName;
            inp.lastName = lastName;
            inp.phoneNumber = phoneNumber;
            inp.emailAddress = email;
            inp.pwd = password;
            // Call ERA to create the User Account
            props.createAccount(inp, props.store, props.branch);
            setCreatingAcct(true);
            isLoggingIn.current = true;
            // Store off the selected Vehicle for later use
            if (props.selectedVehicle) {
                setTempSelVeh(props.selectedVehicle);
            }
            const ascCtaInteractionInp : ascCtaInteractionInput = {
                pageType: 'contact',
                elementText: strings.CreateAcct,
                elementColor: '',
                elementOrder: '',
                elementType: 'search_tool',
                elementSubType: 'nav_button',
                eventAction: 'click',
                eventActionResult: 'next_step',
                linkUrl: '',
            };
            ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
            const ascFormEngagementInp: ascFormEngagementInput = {
                pageType: 'contact',
                commType: 'form',
                commStatus: 'engage',
                elementText: strings.CreateAcct,
                department: 'service',
                formName: 'Service_Portal_2',
                formType: 'service_appointment',
            };
            ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.createAccount, props.store, props.branch, props.vehicles, props.selectedVehicle,
        firstName, lastName, phoneNumber, email, password]);

    useEffect(() => {
        // If we are resuming the appointment add process after creating an account, then pop the sign up screen
        if (resumeAppt && currentScreen === ComponentMapIds.SignUp) {
            setResumeDone(true);
            setResumeAppt(false);
            if (previousScreen === ComponentMapIds.Login) {
                props.popFromMainPane([currentScreen, previousScreen]);
            } else {
                props.popFromMainPane(currentScreen);
            }
            // If we stored off a selected vehicle, then set the log selected vehicle
            if (tempSelVeh) {
                props.setApptHistId(tempSelVeh);
            }
        }
    }, [resumeAppt, props.popFromMainPane, currentScreen, previousScreen, tempSelVeh, props.setApptHistId]);

    useEffect(() => {
        if (props?.customerInfo && props.customerInfo.nameId && (creatingAcct || props.identityVerified) &&
        (currentScreen === ComponentMapIds.SignUp || currentScreen === ComponentMapIds.VerifyIdentity) && !resumeAppt &&
        !resumeDone) {
            setCreatingAcct(false);
            props.clearVehicles();
            const loginInfo = {
                firstName: firstName,
                lastName: lastName,
                phoneNumber: phoneNumber,
                emailAddress: email,
                pwd: password,
                identityVerified: true,
            };
            if (props.previewFlag !== 'p') {
                props.onLogin(loginInfo, props.profileNum, props.store, props.branch);
            }
            setLoggingIn(true);

            if (isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceAccountCreated', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                });
            }
        }
    }, [props.customerInfo, props.previewFlag, props.onLogin, props.profileNum, props.store, props.branch,
        creatingAcct, phoneNumber, email, password, props.identityVerified,
        currentScreen, firstName, lastName, props.clearVehicles, resumeAppt, resumeDone, loggingIn, props.sessionId,
        props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched,
        isSDAnalyticsEnabled]);

    useEffect(() => {
        let onSummary = false;
        for (let i = 0; i < props.mainPaneViewOrder.length; i++) {
            const mainPaneViewIds = props.mainPaneViewOrder[i].viewId;
            if (mainPaneViewIds === 'Summary') {
                onSummary = true;
            }
        }
        if (loggingIn && props.customerInfo?.login) {
            setLoggingIn(false);
            const apptCustInp: UpdateApptCustInp = {
                nameId: props.customerInfo.nameId,
                firstName: props.customerInfo.firstName,
                lastName: props.customerInfo.lastName,
                emailAddress: props.customerInfo.emailAddress,
                phoneNumber: props.customerInfo.phoneNumber,
                phoneType: props.customerInfo.phoneType,
                prefContact: props.customerInfo.prefContact,
                loggedIn: props.customerInfo.login,
            };
            props.onAppCustUpdate(apptCustInp);
            if (!onSummary) {
                props.getVehicles();
            }
            if (props.customerInfo.vehicleList && props.identityVerified) {
                const vehicles = props.customerInfo.vehicleList;
                if (vehicles.length >= 1) {
                    props.selectVehicle(vehicles[0].id);

                    if (onSummary) {
                        props.setViewMode(ViewMode.Appt);
                        setResumeAppt(true);

                        return;
                    } else {
                        props.setViewMode(ViewMode.CustAcct);
                        props.pushToMainPanes({
                            viewId: ComponentMapIds.UserAccountMenu,
                            component: ComponentMapIds.UserAccountMenu,
                        });

                        return;
                    }
                }
            }
            if (onSummary) {
                setResumeAppt(true);

                return;
            } else {
                // If we get here while adding an appointment, but we aren't on the confirmation screen,
                // we should return to our place in the appointment process.
                if (props.viewMode === ViewMode.Appt && previousScreen !== ComponentMapIds.LandingPage) {
                    setResumeAppt(true);
                } else {
                    if (props.customerInfo.firstName) {
                        props.setViewMode(ViewMode.CustAcct);
                        props.pushToMainPanes({
                            viewId: ComponentMapIds.UserAccountMenu,
                            component: ComponentMapIds.UserAccountMenu,
                        });
                    }
                }
            }
        } else {
            if (loggingIn && currentScreen === ComponentMapIds.SignUp && props.mfaContact.length > 0 && props.mfaEnabled) {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.VerifyIdentity,
                    component: ComponentMapIds.VerifyIdentity,
                });
                setLoggingIn(false);
            }
        }
    }, [loggingIn, props.customerInfo, props.onAppCustUpdate, props.getVehicles, props.selectVehicle,
        currentScreen, props.mfaEnabled, props.pushToMainPanes, props.setViewMode, previousScreen,
        props.mainPaneViewOrder, props.identityVerified, props.mfaContact, props.popFromMainPane, props.viewMode,
        props.setApptHistId, tempSelVeh]);

    // Set Proceed button to disabled by default.  Require First, Last, Phone Number, Email, and Password
    let createDisabled = true;
    if (firstName !== '' && lastName !== '' && phoneNumber !== '' &&
        email !== '' && password !== '' && confirm !== '' && password.length >= 6 &&
        password.length <= 10 && password === confirm && email.match(emailRegex) &&
        (firstName + lastName).length <= 35 && !errors['PhoneNumber'].error &&
        !nameLengthError && !errors['Email'].error && email.length < 63) {
        createDisabled = false;
    }

    // Switch back to the login page.
    const loginHandler = useCallback(() => {
        setShowAccountError(false);
        props.clearError();
        props.pushToMainPanes({
            viewId: ComponentMapIds.Login,
            component: ComponentMapIds.Login,
        });
    }, [props.pushToMainPanes, props.clearError]);

    const closeHandler = useCallback((e: any) => {
        setShowAccountError(false);
        props.clearError();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'contact',
            elementText: 'x',
            elementColor: '',
            elementOrder: '',
            elementType: 'search_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, props.vehicles, props.selectedVehicle]);

    useEffect(() => {
        if (creatingAcct && props.errMsg) {
            setShowAccountError(true);
            setCreatingAcct(false);
        }
    }, [creatingAcct, props.errMsg]);

    const tryAgainHandler = useCallback((e: any) => {
        props.clearError();
        createAcct(e);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'contact',
            elementText: strings.TryAgain,
            elementColor: '',
            elementOrder: '',
            elementType: 'search_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, createAcct, props.vehicles, props.selectedVehicle]);

    const resetPassHandler = useCallback(() => {
        setShowAccountError(false);
        props.clearError();
        props.sendResetEmail(email, '', props.profileNum, props.store, props.branch);
        setResetEmailSent(true);
    }, [props.clearError, props.sendResetEmail, email, props.profileNum, props.store, props.branch]);

    const accountError = useMemo(() => {
        if (showAccountError && props.errMsg) {
            if (props.errMsg === ' Customer Record in use') {
                return <RecordLockError
                    headerMsg={strings.Oops}
                    message={strings.MessageAdding + strings.AcctTimeLit}
                    primaryHandler={tryAgainHandler}
                    secondaryHandler={closeHandler}
                    closeHandler={closeHandler}
                />;
            }
            const errMsgLit = props.errMsg;
            const errBtn1 = resetPassHandler;
            const errBtn2 = loginHandler;
            const btn1Lit = strings.ResetPass;
            const btn2Lit = strings.Login;
            const header = strings.AccountExists;

            return GetErrorMessage(errBtn1, errBtn2, closeHandler, errMsgLit, header, btn1Lit, btn2Lit);
        }
        if (props.errMsg === '1006') {
            const errMsgLit = strings.ResetPasswordContactDeal;
            const errBtn1 = closeHandler;
            const errBtn2 = closeHandler;
            const btn1Lit = '';
            const btn2Lit = strings.Okay;
            const header = strings.ContactDealer;

            return GetErrorMessage(errBtn1, errBtn2, closeHandler, errMsgLit, header, btn1Lit, btn2Lit);
        }

        return null;
    }, [showAccountError, props?.errMsg, closeHandler, loginHandler, tryAgainHandler, resetPassHandler]);

    // check the reset password email error and if it's sent to display the Dialog
    useEffect(() => {
        if (props.errMsg === '0000' && resetEmailSent) {
            setDisplaySentMessage(true);
        } else {
            setDisplaySentMessage(false);
        }
    }, [props.errMsg, resetEmailSent]);

    const resetCloseHandler = useCallback((e: any) => {
        props.clearError();
        setDisplaySentMessage(false);
        setResetEmailSent(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'contact',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, props.vehicles, props.selectedVehicle]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Sign Up', '');
    }, [
        props.addLogEntry,
    ]);

    useEffect(() => {
        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length -1].viewId;
        if (currentPane === ComponentMapIds.SignUp) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'contact',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, props.vehicles, props.selectedVehicle]);
    // Reset Email Sent Dialog
    const resetSentMessage = <Dialog
        id='ResetEmailSent'
        cssClasses={{
            content: css.content,
            paper: css.paper,
            header: css.LightBoxHeader,
        }}
        isOpen={displaySentMessage}
        anchorOrigin={{
            vertical: 'top',
        }}
        header={strings.EmailSent}
        hideSave={true}
        hideCancel={true}
        disableUpperCase={true}
        onCancel={resetCloseHandler}
        onBackDropClick={resetCloseHandler}
        width={'full'}>
        <div>
            <div className={css.headerbox}>
                {strings.EmailSent}
                <div className={css.headerIcon}>
                    <IconRemove id='closeBtn' onClick={resetCloseHandler}/>
                </div>
            </div>
            {strings.formatString(strings.ResetPassEmailSent, email).toString()}
            <hr className={css.hrLightBox}/>
            <div className={css.btnArea}>
                <SVPButton
                    id='Proceed'
                    handler={resetCloseHandler}
                    message={strings.Okay}
                    noArrow
                    variant={'primaryBtnAlt'}
                    theme={props.theme}
                />
            </div>
        </div>
    </Dialog>;

    return (
        <div>
            <div className={css.mainDiv}>
                {resetSentMessage}
                <ScrollBar scrollVertical>

                    <h2>{strings.SignUp}</h2>
                    <h3>{strings.FieldsCreateAcct}</h3>
                    <hr />
                    {accountError}
                    <div className={css.signUp}>
                        <span className={css.reqField}>{strings.RequiredFields}</span>
                        <div className={css.input}>
                            <TextField
                                id='FirstName'
                                label={strings.FirstName}
                                onChange={changeHandler}
                                onBlur={blurHandler}
                                value={firstName}
                                type='text'
                                size='full'
                                error={nameLengthError}
                                helperText={nameLengthError ? strings.ValidNameLength : ''}
                            />
                        </div>
                        <div className={css.input}>
                            <TextField
                                id='LastName'
                                label={strings.LastName}
                                onChange={changeHandler}
                                onBlur={blurHandler}
                                value={lastName}
                                type='text'
                                size='full'
                                error={nameLengthError}
                                helperText={nameLengthError ? strings.ValidNameLength : ''}
                            />
                        </div>
                        <div className={css.input}>
                            <PhoneField
                                id='PhoneNumber'
                                label={strings.PhoneNumber}
                                onChange={changeHandler}
                                onBlur={blurHandler}
                                value={phoneNumber}
                                size='full'
                            />
                        </div>
                        <div className={css.input}>
                            <EmailField
                                id='Email'
                                label={strings.EmailAddress}
                                onChange={changeHandler}
                                onBlur={blurHandler}
                                value={email}
                                size='full'
                                maxLength={62}
                            />
                        </div>
                        <div className={css.Input2}>
                            <PasswordField
                                id='Password'
                                label={strings.Password}
                                onChange={changeHandler}
                                onBlur={blurHandler}
                                value={password}
                                size='full'
                                minLength={6}
                                maxLength={10}
                            />
                        </div>
                        <div className={css.Input2}>
                            <TextField
                                id='Confirm'
                                label={strings.ConfirmPassword}
                                onChange={changeHandler}
                                onBlur={blurHandler}
                                value={confirm}
                                size='full'
                                type='password'
                                error={errors['Confirm'].error}
                                helperText={errors['Confirm'].helperText}
                            />
                        </div>
                        <div className={css.create}>
                            <SVPButton
                                id='createBtn'
                                variant={'primaryBtnAlt'}
                                handler={createAcct}
                                message={strings.CreateAcct}
                                noArrow={true}
                                isDisabled={createDisabled}
                                tooltip={strings.CreateAcctTip}
                                theme={props.theme}
                            />
                        </div>
                        {creatingAcct ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
                        {/* <hr className={css.createHr} /> */}
                        {/* <div className={css.login}>
                            <span className={css.loginMsg} >{strings.HaveAccount}</span>
                            <SVPButton
                                id='loginBtn'
                                variant={'secondaryBtnAlt'}
                                handler={loginHandler}
                                message={strings.Login}
                                noArrow={true}
                                tooltip={strings.LogInTitle}
                                theme={props.theme}
                            />
                        </div> */}
                    </div>
                </ScrollBar>
            </div>
        </div>
    );
};

function GetErrorMessage(errBtn1: (e: any) => void, errBtn2: (e: any) => void, closeHandler: (e: any) => void,
    errMsg: string, header: string, btn1Lit, btn2Lit) {
    return (
        <ErrorMessage
            headerMsg={header}
            errorMsg={errMsg}
            primaryBtnMessage={btn2Lit}
            secondaryBtnMessage={btn1Lit}
            primaryHandler={errBtn2}
            secondaryHandler={errBtn1}
            closeHandler={closeHandler}
        />
    );
}

export default SignUp;